import { useState } from "react";
import { Key } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";

// Oct 26 2026, the user's choice: the password an admin typed works for one
// sign-in only - the operator sets his own here before he reaches anything.
export default function ChangePasswordPage() {
  const { user, changePassword, logout } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [again, setAgain] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const mismatch = !!again && next !== again;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await changePassword(current, next);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0E7C86] flex items-center justify-center p-4" data-testid="change-password-page">
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-[0_20px_60px_-15px_rgba(0,0,0,0.4)] p-8 flex flex-col items-center gap-5">
        <div className="w-14 h-14 rounded-xl bg-[#0E7C86]/10 flex items-center justify-center">
          <Key size={28} weight="fill" className="text-[#0E7C86]" />
        </div>
        <div className="text-center">
          <h1 className="font-heading text-xl font-bold text-[#101828]">Set your own password</h1>
          <p className="text-sm text-[#667085] mt-1">
            {user?.name ? `${user.name}, the ` : "The "}password you were given works once. Choose one only you know.
          </p>
        </div>
        <form className="w-full space-y-2" onSubmit={submit}>
          <Input
            className="h-11" type="password" placeholder="Password you were given"
            value={current} onChange={(e) => setCurrent(e.target.value)}
            data-testid="change-password-current"
          />
          <Input
            className="h-11" type="password" placeholder="New password (6 characters or more)"
            value={next} onChange={(e) => setNext(e.target.value)}
            data-testid="change-password-new"
          />
          <Input
            className="h-11" type="password" placeholder="New password again"
            value={again} onChange={(e) => setAgain(e.target.value)}
            data-testid="change-password-again"
          />
          {mismatch && (
            <p className="text-xs text-[#B54708]" data-testid="change-password-mismatch">
              The two new passwords are not the same.
            </p>
          )}
          <Button
            type="submit"
            disabled={busy || !current || next.length < 6 || mismatch}
            className="w-full h-11 bg-[#0E7C86] hover:bg-[#0B6B74] font-bold rounded-lg"
            data-testid="change-password-submit"
          >
            {busy ? "Saving..." : "Save and continue"}
          </Button>
          {error && (
            <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-2" data-testid="change-password-error">
              {error}
            </p>
          )}
        </form>
        <button type="button" onClick={logout} className="text-xs text-[#667085] hover:underline" data-testid="change-password-signout">
          Sign out
        </button>
      </div>
    </div>
  );
}
